"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Search,
  Plus,
  Euro,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Trash2,
  Clock,
  ArrowRight,
  TrendingUp,
  Edit2,
} from "lucide-react";
import {
  createInvoiceAction,
  updateInvoiceStatusAction,
  deleteInvoiceAction,
  createPaymentAction,
  updateInvoiceAction,
} from "@/actions/finance-actions";

interface InvoiceItem {
  id: string;
  amount: number;
  dueDate: string;
  status: "draft" | "sent" | "paid" | "overdue" | "contested";
  projectId: string | null;
  projectTitle: string | null;
  createdAt: string;
  totalPaid: number;
  totalPending: number;
}

interface ProjectItem {
  id: string;
  title: string;
}

interface InvoicesClientProps {
  initialInvoices: InvoiceItem[];
  projects: ProjectItem[];
}

export function InvoicesClient({
  initialInvoices,
  projects,
}: InvoicesClientProps) {
  const router = useRouter();
  const [invoicesList, setInvoicesList] =
    useState<InvoiceItem[]>(initialInvoices);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [activeKpiFilter, setActiveKpiFilter] = useState<
    "all" | "paid" | "pending" | "overdue"
  >("all");

  // Invoice Modal Form State
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceItem | null>(
    null,
  );
  const [newProjectId, setNewProjectId] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [newStatus, setNewStatus] = useState<any>("draft");

  // Payment Modal Form State
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceItem | null>(
    null,
  );
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<"pending" | "cleared">(
    "cleared",
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Sync state with server props
  React.useEffect(() => {
    setInvoicesList(initialInvoices);
  }, [initialInvoices]);

  const handleOpenInvoiceModal = () => {
    setEditingInvoice(null);
    setNewProjectId("");
    setNewAmount("");
    setNewDueDate(
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
    ); // 30 days default
    setNewStatus("draft");
    setError("");
    setIsInvoiceModalOpen(true);
  };

  const handleOpenEditModal = (invoice: InvoiceItem) => {
    setEditingInvoice(invoice);
    setNewProjectId(invoice.projectId || "");
    setNewAmount(invoice.amount.toString());
    setNewDueDate(invoice.dueDate.split("T")[0]);
    setNewStatus(invoice.status);
    setError("");
    setIsInvoiceModalOpen(true);
  };

  const handleOpenPaymentModal = (invoice: InvoiceItem) => {
    setSelectedInvoice(invoice);
    const outstanding = Math.max(0, invoice.amount - invoice.totalPaid);
    setPaymentAmount(outstanding.toString());
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setPaymentStatus("cleared");
    setError("");
    setIsPaymentModalOpen(true);
  };

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const amountNum = parseFloat(newAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("L'importo deve essere un numero valido maggiore di 0.");
      return;
    }
    if (!newDueDate) {
      setError("La data di scadenza è obbligatoria.");
      return;
    }

    setLoading(true);
    try {
      if (editingInvoice) {
        const res = await updateInvoiceAction(editingInvoice.id, {
          projectId: newProjectId || null,
          amount: amountNum,
          dueDate: newDueDate,
          status: newStatus,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsInvoiceModalOpen(false);
          router.refresh();
        }
      } else {
        const res = await createInvoiceAction({
          projectId: newProjectId || null,
          amount: amountNum,
          dueDate: newDueDate,
          status: newStatus,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsInvoiceModalOpen(false);
          router.refresh();
        }
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio della fattura.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!selectedInvoice) return;

    const amountNum = parseFloat(paymentAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("L'importo del pagamento deve essere maggiore di 0.");
      return;
    }

    setLoading(true);
    try {
      const res = await createPaymentAction({
        invoiceId: selectedInvoice.id,
        amount: amountNum,
        paymentDate: paymentDate || undefined,
        status: paymentStatus,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsPaymentModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante la registrazione del pagamento.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (id: string) => {
    if (
      !confirm(
        "Sei sicuro di voler eliminare questa fattura? Tutti i pagamenti associati verranno cancellati.",
      )
    )
      return;
    try {
      const res = await deleteInvoiceAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'eliminazione: " + err.message);
    }
  };

  const handleChangeStatus = async (id: string, status: any) => {
    try {
      const res = await updateInvoiceStatusAction(id, status);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'aggiornamento: " + err.message);
    }
  };

  const now = new Date();

  const filteredInvoices = invoicesList.filter((inv) => {
    const matchesSearch =
      inv.id.toLowerCase().includes(search.toLowerCase()) ||
      (inv.projectTitle &&
        inv.projectTitle.toLowerCase().includes(search.toLowerCase()));

    const matchesStatus = statusFilter === "all" || inv.status === statusFilter;

    const isOverdue = inv.status !== "paid" && new Date(inv.dueDate) < now;
    const matchesKpi =
      activeKpiFilter === "all" ||
      (activeKpiFilter === "paid" && inv.status === "paid") ||
      (activeKpiFilter === "pending" && inv.status !== "paid") ||
      (activeKpiFilter === "overdue" && isOverdue);

    return matchesSearch && matchesStatus && matchesKpi;
  });

  // Aggregates
  const totalInvoiced = invoicesList.reduce((sum, inv) => sum + inv.amount, 0);
  const totalCollected = invoicesList.reduce(
    (sum, inv) => sum + inv.totalPaid,
    0,
  );
  const totalOutstanding = invoicesList.reduce(
    (sum, inv) => sum + Math.max(0, inv.amount - inv.totalPaid),
    0,
  );

  const totalOverdue = invoicesList
    .filter((inv) => inv.status !== "paid" && new Date(inv.dueDate) < now)
    .reduce((sum, inv) => sum + Math.max(0, inv.amount - inv.totalPaid), 0);

  const getStatusBadge = (status: InvoiceItem["status"]) => {
    switch (status) {
      case "draft":
        return <Badge variant="neutral">Bozza</Badge>;
      case "sent":
        return <Badge variant="info">Inviata</Badge>;
      case "paid":
        return <Badge variant="success">Pagata</Badge>;
      case "overdue":
        return <Badge variant="error">Scaduta</Badge>;
      case "contested":
        return <Badge variant="warning">Contestata</Badge>;
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat("it-IT", { dateStyle: "medium" }).format(
      new Date(dateStr),
    );
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card
          onClick={() => setActiveKpiFilter("all")}
          className={`transition-all border-l-4 border-l-category-finance cursor-pointer select-none hover:shadow-sm ${
            activeKpiFilter === "all"
              ? "ring-2 ring-category-finance border-category-finance bg-category-finance/5"
              : "hover:border-primary-500/30"
          }`}
        >
          <CardHeader className="pb-2">
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              Fatturato Totale
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold tracking-tight">
                {formatCurrency(totalInvoiced)}
              </span>
              <TrendingUp className="h-5 w-5 text-category-finance" />
            </div>
            <p className="text-[10px] text-text-muted mt-1">
              Totale emesso dall'inizio dell'anno
            </p>
          </CardContent>
        </Card>

        <Card
          onClick={() => setActiveKpiFilter("paid")}
          className={`transition-all border-l-4 border-l-success cursor-pointer select-none hover:shadow-sm ${
            activeKpiFilter === "paid"
              ? "ring-2 ring-success-600 border-success-500 bg-success-500/[0.02]"
              : "hover:border-success-500/30"
          }`}
        >
          <CardHeader className="pb-2">
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              Incassato
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold tracking-tight text-success">
                {formatCurrency(totalCollected)}
              </span>
              <CheckCircle2 className="h-5 w-5 text-success" />
            </div>
            <p className="text-[10px] text-text-muted mt-1">
              {totalInvoiced > 0
                ? `${Math.round((totalCollected / totalInvoiced) * 100)}% del totale`
                : "Nessuna fattura"}
            </p>
          </CardContent>
        </Card>

        <Card
          onClick={() => setActiveKpiFilter("pending")}
          className={`transition-all border-l-4 border-l-warning cursor-pointer select-none hover:shadow-sm ${
            activeKpiFilter === "pending"
              ? "ring-2 ring-warning-600 border-warning-500 bg-warning-500/[0.02]"
              : "hover:border-warning-500/30"
          }`}
        >
          <CardHeader className="pb-2">
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              In Attesa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold tracking-tight text-warning">
                {formatCurrency(totalOutstanding)}
              </span>
              <Clock className="h-5 w-5 text-warning" />
            </div>
            <p className="text-[10px] text-text-muted mt-1">
              Importi in attesa di riscossione
            </p>
          </CardContent>
        </Card>

        <Card
          onClick={() => setActiveKpiFilter("overdue")}
          className={`transition-all border-l-4 border-l-error cursor-pointer select-none hover:shadow-sm ${
            activeKpiFilter === "overdue"
              ? "ring-2 ring-error-600 border-error-500 bg-error-500/[0.02]"
              : "hover:border-error-500/30"
          }`}
        >
          <CardHeader className="pb-2">
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              Scaduto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold tracking-tight text-error">
                {formatCurrency(totalOverdue)}
              </span>
              <AlertCircle className="h-5 w-5 text-error animate-pulse" />
            </div>
            <p className="text-[10px] text-text-muted mt-1">
              Fatture non pagate oltre la scadenza
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca fattura o progetto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex w-full sm:w-auto gap-3 items-center justify-between sm:justify-end">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-[180px]"
          >
            <option value="all">Tutti gli stati</option>
            <option value="draft">Bozze</option>
            <option value="sent">Inviate</option>
            <option value="paid">Pagate</option>
            <option value="overdue">Scadute</option>
            <option value="contested">Contestate</option>
          </Select>

          <Button onClick={handleOpenInvoiceModal} className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" />
            Nuova Fattura
          </Button>
        </div>
      </div>

      {/* Grid of Invoices */}
      {filteredInvoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <Euro className="h-10 w-10 text-primary-500 mb-3" />
          <h3 className="text-base font-bold text-text-primary">
            Nessuna fattura trovata
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Crea una nuova fattura per iniziare a tracciare i pagamenti.
          </p>
          <Button onClick={handleOpenInvoiceModal} className="mt-4 gap-1.5">
            <Plus className="h-4 w-4" />
            Nuova Fattura
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredInvoices.map((inv) => {
            const isOverdue =
              inv.status !== "paid" && new Date(inv.dueDate) < now;
            const displayStatus = isOverdue ? "overdue" : inv.status;
            const outstanding = Math.max(0, inv.amount - inv.totalPaid);

            return (
              <Card
                key={inv.id}
                className="relative overflow-hidden flex flex-col justify-between hover:border-primary-500/50 transition-all border-l-4 border-l-category-finance group cursor-pointer"
                onClick={() => handleOpenEditModal(inv)}
              >
                <div
                  className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Link
                    href={`/explore?nodeId=${inv.id}`}
                    title="Esplora relazioni"
                  >
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 p-0 flex items-center justify-center"
                    >
                      <TrendingUp className="h-3.5 w-3.5 text-text-secondary hover:text-primary-600" />
                    </Button>
                  </Link>
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => handleOpenEditModal(inv)}
                    title="Modifica"
                    className="h-7 w-7 p-0 flex items-center justify-center"
                  >
                    <Edit2 className="h-3.5 w-3.5 text-text-secondary hover:text-primary-600" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => handleDeleteInvoice(inv.id)}
                    title="Elimina"
                    className="h-7 w-7 p-0 flex items-center justify-center hover:bg-error-soft hover:text-error"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-text-secondary" />
                  </Button>
                </div>

                <CardHeader className="pb-3 pr-24">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <Badge
                        variant="neutral"
                        className="mb-1 font-mono text-[10px]"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <Link
                          href={`/finance/invoices/${inv.id}`}
                          className="hover:text-primary-600 hover:underline"
                        >
                          {inv.id}
                        </Link>
                      </Badge>
                      <h3 className="font-bold text-base text-text-primary leading-snug">
                        {inv.projectTitle ? (
                          <Link
                            href={`/work/projects/${inv.projectId}`}
                            className="hover:text-primary-600 hover:underline"
                            onClick={(e: React.MouseEvent) =>
                              e.stopPropagation()
                            }
                          >
                            {inv.projectTitle}
                          </Link>
                        ) : (
                          <span className="text-text-muted italic">
                            Fattura Generica
                          </span>
                        )}
                      </h3>
                    </div>

                    <div className="flex flex-col items-end gap-1 group-hover:opacity-0 transition-opacity duration-200">
                      <span className="text-base font-bold text-text-primary shrink-0">
                        {formatCurrency(inv.amount)}
                      </span>
                      <div
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        {getStatusBadge(displayStatus as any)}
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 pb-4 flex-1 text-xs text-text-secondary">
                  <div className="flex items-center gap-1.5">
                    <Calendar
                      className={`h-3.5 w-3.5 ${isOverdue ? "text-error" : "text-text-muted"}`}
                    />
                    <span
                      className={isOverdue ? "text-error font-semibold" : ""}
                    >
                      Scadenza: <strong>{formatDate(inv.dueDate)}</strong>
                    </span>
                  </div>

                  {/* Payment Progress */}
                  <div className="space-y-1.5 border-t border-border/40 pt-3">
                    <div className="flex justify-between text-[10px] text-text-muted font-medium">
                      <span>
                        Incassato:{" "}
                        <strong>{formatCurrency(inv.totalPaid)}</strong>
                      </span>
                      <span>
                        {Math.round((inv.totalPaid / inv.amount) * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-border rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-success h-1.5 rounded-full"
                        style={{
                          width: `${Math.min(100, (inv.totalPaid / inv.amount) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Interactive Riscossione Buttons */}
                  <div
                    className="flex items-center justify-between gap-3 pt-3 border-t border-border/40"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    {outstanding > 0 ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleOpenPaymentModal(inv)}
                        className="text-xs h-7 py-0 px-2"
                      >
                        Paga
                      </Button>
                    ) : (
                      <span className="text-success font-semibold">
                        Saldato
                      </span>
                    )}

                    <Select
                      value={inv.status}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleChangeStatus(inv.id, e.target.value as any)
                      }
                      className="h-7 py-0 text-xs w-[110px]"
                    >
                      <option value="draft">Bozza</option>
                      <option value="sent">Inviata</option>
                      <option value="paid">Pagata</option>
                      <option value="contested">Contestata</option>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Invoice Dialog */}
      <Dialog open={isInvoiceModalOpen} onOpenChange={setIsInvoiceModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingInvoice ? "Modifica Fattura" : "Nuova Fattura"}
            </DialogTitle>
            <DialogDescription>
              {editingInvoice
                ? "Aggiorna i dettagli e la scadenza della fattura emessa."
                : "Crea una fattura collegata a un progetto per tracciare le transazioni attive."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateInvoice} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="inv-project">Collegamento Progetto</Label>
              <Select
                id="inv-project"
                value={newProjectId}
                onChange={(e) => setNewProjectId(e.target.value)}
                disabled={loading}
              >
                <option value="">Nessuno (Fattura Generica)</option>
                {projects.map((proj) => (
                  <option key={proj.id} value={proj.id}>
                    {proj.title}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="inv-amount">Importo (€)</Label>
                <Input
                  id="inv-amount"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="inv-status">Stato</Label>
                <Select
                  id="inv-status"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="draft">Bozza</option>
                  <option value="sent">Inviata</option>
                  {editingInvoice && (
                    <>
                      <option value="paid">Pagata</option>
                      <option value="contested">Contestata</option>
                    </>
                  )}
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-duedate">Data di Scadenza</Label>
              <Input
                id="inv-duedate"
                type="date"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsInvoiceModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingInvoice ? "Salva Modifiche" : "Crea Fattura"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Log Payment Dialog */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Pagamento</DialogTitle>
            <DialogDescription>
              Aggiungi un pagamento per la fattura{" "}
              <strong className="font-mono text-xs">
                {selectedInvoice?.id}
              </strong>
              .
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreatePayment} className="space-y-4 my-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="pay-amount">Importo Pagamento (€)</Label>
                <Input
                  id="pay-amount"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pay-status">Stato Transazione</Label>
                <Select
                  id="pay-status"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="cleared">Saldata / Liquidata (Cleared)</option>
                  <option value="pending">
                    In Sospeso / Pendente (Pending)
                  </option>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pay-date">Data Pagamento</Label>
              <Input
                id="pay-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsPaymentModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={loading}
                className="bg-success hover:bg-success-600 text-white"
              >
                Registra Pagamento
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
